import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FiArrowRight, FiMap } from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/layout/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import Seo, { organizationSchema } from '../components/Seo';
import { Container } from '../components/ui';
import LocationHero from '../components/location/LocationHero';
import LocationLedger from '../components/location/LocationLedger';
import { LocationHighlights, LocationIntro } from '../components/location/LocationStory';
import LocationListings from '../components/location/LocationListings';
import LocationMap from '../components/location/LocationMap';
import LocationFaq from '../components/location/LocationFaq';
import LocationContact from '../components/location/LocationContact';
import {
  buildStatCells,
  defaultTitle,
  pinsFromProperties,
  whatsAppHref,
} from '../components/location/locationModel';
import { actionBase, ink, sand } from '../components/location/tokens';
import { locationService } from '../services/locations';
import { buildImageUrl } from '../services/api';
import { useSettings } from '../contexts/SettingsContext';
import { trackWhatsAppClick } from '../utils/tracking';
import { withOriginCode } from '../utils/siteEvents';
import { isMissingHttpError } from '../utils/http';
import { LocationPage as LocationPageData } from '../types/location';

type LoadState = 'loading' | 'ready' | 'missing' | 'failed';

const shimmer = keyframes`
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
`;

const HeroSkeleton = styled.div`
  height: clamp(560px, 82vh, 780px);
  background: linear-gradient(90deg, ${({ theme }) => ink(theme)} 25%, #3a2a1c 37%,
    ${({ theme }) => ink(theme)} 63%);
  background-size: 1200px 100%;
  animation: ${shimmer} 1.6s ease infinite;
`;

const Missing = styled.section`
  padding: clamp(4rem, 12vw, 8rem) 0;
  background: ${({ theme }) => sand(theme)};
  text-align: center;

  h1 {
    margin: 1rem auto 0;
    max-width: 18ch;
    font-family: ${({ theme }) => theme.fonts.heading};
    font-weight: 600;
    font-size: clamp(2rem, 5vw, 3.2rem);
    line-height: 1.08;
    letter-spacing: -0.03em;
    color: ${({ theme }) => ink(theme)};
  }

  p {
    margin: 1rem auto 0;
    max-width: 46ch;
    color: ${({ theme }) => theme.colors.neutral[600]};
    line-height: 1.7;
  }
`;

const MissingKicker = styled.span`
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.neutral[500]};
`;

const MissingActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.8rem;
  margin-top: 2rem;
`;

const DarkAction = styled(Link)`
  ${actionBase}
  background: ${({ theme }) => ink(theme)};
  color: #fff;
`;

const LightAction = styled(Link)`
  ${actionBase}
  border: 1px solid ${({ theme }) => ink(theme)};
  color: ${({ theme }) => ink(theme)};
`;

/* O rodapé tem margem própria; aqui a faixa escura de contato encosta nele. */
const Main = styled.main`
  margin-bottom: -4rem;
`;

const companyOf = (settings: any): string =>
  settings?.company_info?.name || settings?.site_name || 'Imobiliária';

const describe = (page: LocationPageData): string => {
  if (page.seo_description) return page.seo_description;
  if (page.headline) return page.headline;
  const place = page.city ? `${page.name}, ${page.city}` : page.name;
  return `Imóveis em ${place}: fotos, valores, mapa e atendimento pelo WhatsApp.`;
};

const LocationPage: React.FC = () => {
  const { slug = '' } = useParams();
  const { pathname } = useLocation();
  const { siteSettings } = useSettings();
  const [page, setPage] = useState<LocationPageData | null>(null);
  const [state, setState] = useState<LoadState>('loading');

  useEffect(() => {
    let active = true;
    setState('loading');
    setPage(null);
    locationService
      .getBySlug(slug)
      .then((data) => {
        if (!active) return;
        setPage(data);
        setState('ready');
      })
      .catch((error) => {
        if (active) setState(isMissingHttpError(error) ? 'missing' : 'failed');
      });
    window.scrollTo(0, 0);
    return () => {
      active = false;
    };
  }, [slug]);

  const company = companyOf(siteSettings);
  const cells = useMemo(() => (page ? buildStatCells(page.stats) : []), [page]);
  const pins = useMemo(() => (page ? pinsFromProperties(page.properties) : []), [page]);
  const center = useMemo<[number, number] | null>(
    () => (page?.latitude != null && page?.longitude != null ? [page.latitude, page.longitude] : null),
    [page]
  );

  if (page && pathname.replace(/\/$/, '') !== page.path) {
    return <Navigate to={page.path} replace />;
  }

  const whatsHref = page
    ? whatsAppHref(
        siteSettings?.company_info?.whatsapp,
        withOriginCode(
          `Olá! Vi no site a página "${page.name}" e gostaria de mais informações. ${window.location.origin}${page.path}`
        )
      )
    : null;
  const onWhatsApp = () => trackWhatsAppClick('location_page');

  return (
    <>
      {page && (
        <Seo
          title={page.seo_title || defaultTitle(page.name, company)}
          description={describe(page).slice(0, 158)}
          canonicalPath={page.path}
          image={page.hero_image ? buildImageUrl(page.hero_image) : undefined}
          structuredData={[
            organizationSchema(siteSettings),
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Início', item: `${window.location.origin}/` },
                { '@type': 'ListItem', position: 2, name: 'Imóveis', item: `${window.location.origin}/imoveis` },
                { '@type': 'ListItem', position: 3, name: page.name, item: `${window.location.origin}${page.path}` },
              ],
            },
          ]}
        />
      )}
      {state === 'missing' && (
        <Seo
          title={`Local não encontrado | ${company}`}
          description="A página solicitada não foi encontrada ou não está publicada."
          robots="noindex,nofollow"
        />
      )}
      <Header />
      <WhatsAppButton />
      <Main>
        {state === 'loading' && <HeroSkeleton aria-busy="true" aria-label="Carregando" />}

        {(state === 'missing' || state === 'failed') && (
          <Missing>
            <Container>
              <MissingKicker>{state === 'missing' ? 'Página não encontrada' : 'Falha ao carregar'}</MissingKicker>
              <h1>
                {state === 'missing'
                  ? 'Este local não está disponível no site'
                  : 'Não foi possível carregar esta página agora'}
              </h1>
              <p>
                {state === 'missing'
                  ? 'O endereço pode ter mudado. Os imóveis continuam no catálogo e no mapa.'
                  : 'Tente de novo em instantes ou veja os imóveis pelo catálogo.'}
              </p>
              <MissingActions>
                <DarkAction to="/imoveis">Ver imóveis <FiArrowRight size={16} /></DarkAction>
                <LightAction to="/mapa"><FiMap size={16} /> Buscar no mapa</LightAction>
              </MissingActions>
            </Container>
          </Missing>
        )}

        {state === 'ready' && page && (
          <>
            <LocationHero page={page} whatsHref={whatsHref} onWhatsApp={onWhatsApp} />
            <LocationLedger cells={cells} />
            {/* Imóveis antes do texto: no celular eles ficavam ~2000px abaixo. */}
            <LocationListings name={page.name} properties={page.properties} />
            <LocationIntro name={page.name} kind={page.kind} intro={page.intro} />
            <LocationHighlights name={page.name} kind={page.kind} items={page.highlights} />
            {center && <LocationMap name={page.name} city={page.city} center={center} pins={pins} />}
            <LocationFaq name={page.name} items={page.faq} />
            <LocationContact
              name={page.name}
              whatsHref={whatsHref}
              phone={siteSettings?.company_info?.phone}
              onWhatsApp={onWhatsApp}
            />
          </>
        )}
      </Main>
      <Footer />
    </>
  );
};

export default LocationPage;
